'use strict';

import accounting from 'accounting';
import Calculator from '../helpers/calculator';

export interface IFormatter {
    currencySymbol: string;

    thousandSeparator: string;

    decimalSeparator: string;

    asDecimal: (value: any, decimals?: number) => string;

    asInteger: (value: any) => string;

    asCurrency: (value: any, symbol?: string, trimEmptyDecimal?: boolean) => string;
}

export default class Formatter implements IFormatter {
    protected innerCurrencySymbol: string = '';

    protected innerThousandSeparator: string = '';

    protected innerDecimalSeparator: string = '';

    constructor(params: { currencySymbol?: string | null, decimalSeparator?: string | null, thousandSeparator?: string | null } = {}) {
        const { currencySymbol, decimalSeparator, thousandSeparator } = params;
        this.innerCurrencySymbol = currencySymbol || '%s';
        this.innerDecimalSeparator = decimalSeparator || ',';
        this.innerThousandSeparator = thousandSeparator || '';
    }

    /**
     * @return {string} The default system symbol
     */
    public get currencySymbol(): string {
        return this.innerCurrencySymbol;
    }

    /**
     * @return {string} the character displayed as the thousands separator (also called grouping separator) character when formatting a number.
     */
    public get thousandSeparator(): string {
        return this.innerThousandSeparator;
    }

    /**
     * @return {string} the character displayed as the decimal point when formatting a number.
     */
    public get decimalSeparator(): string {
        return this.innerDecimalSeparator;
    }

    /**
     * Formats the value as a decimal number.
     *
     * @param value the value to be formatted
     * @param decimals the number of digits after the decimal point
     * @return string the formatted result
     */
    public asDecimal(value: any, decimals: number = 2): string {
        return accounting.formatNumber(this.normalizeNumericValue(value), decimals, this.thousandSeparator, this.decimalSeparator);
    }

    /**
     * Formats the value as an integer number by removing any decimal digits without rounding
     *
     * @param value the value to be formatted
     * @return string the formatted result
     */
    public asInteger(value: any): string {
        return accounting.formatNumber(this.normalizeNumericValue(value), 0, this.thousandSeparator, this.decimalSeparator);
    }

    /**
     * Formats the value as a currency number.
     *
     * @param {*} value
     * @param {string} [symbol]
     * @param {boolean} [trimEmptyDecimal]
     * @return string the formatted result
     */
    public asCurrency(value: any, symbol: string | null = null, trimEmptyDecimal: boolean = true): string {
        if (!symbol) {
            symbol = this.currencySymbol;
        }
        let result = this.asDecimal(value, 2);
        if (trimEmptyDecimal) {
            result = result.replace(new RegExp(this.decimalSeparator + '00$'), '');
        }

        return symbol.replace('%s', result);
    }

    /**
     * Formats the value as a currency number with lead indication.
     *
     * @param {*} value
     * @param {string} [symbol]
     * @param {boolean} [trimEmptyDecimal]
     * @return string the formatted result
     */
    public asRatio(value: any, symbol: string | null = null, trimEmptyDecimal: boolean = true): string {
        const v = this.normalizeNumericValue(value);

        if (v === 0) {
            return '0';
        }

        return `${ v > 0 ? '+' : '-' } ${ this.asCurrency(Math.abs(v), symbol, trimEmptyDecimal) }`;
    }

    /**
     * Normalizes a numeric input value
     */
    private normalizeNumericValue(value: any): number {
        if (!Calculator.isNumeric(value)) {
            return 0;
        }

        return parseFloat(value);
    }
}
